import { Breadcrumbs, Header, LoadingState, View, Workspace } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AmendmentsTab from "./AmendmentsTab";
import ContactsTab from "./ContactsTab";
import DetailsTab from "./DetailsTab";
import TenanciesTab from "./TenanciesTab";
import TenantsTab from "./TenantsTab";
import TreeTab from "./TreeTab";
import { useFeatureFlagGuard } from "v2/hooks";

/**
 * One lease — the YARDI side, `LeaseAgreement`
 * (GET /v2/lease-agreements/:id, V2::LeaseAgreementsController#show).
 *
 * AMENDMENTS IS THE DEFAULT TAB, because `Tabs` selects the first one it is
 * given (or whatever the URL hash names). That is the right default: an
 * amendment is the unit of work everything else hangs off, and the lease's own
 * fields are reference material.
 *
 * Every tab but Details fetches its own rows, the way the project workspace
 * tabs do. This request is only for the lease's fields and the page title, so
 * it deliberately does NOT `include` the amendments — the Amendments tab asks
 * for them itself, and including them here would load the whole tree on every
 * visit to any tab.
 */

const Show = () => {
	const { id } = useParams<{ id: string }>();
	// null = still loading; false = the API refused, or the record is gone.
	const [agreement, setAgreement] = useState<tcpinpoint.LeaseAgreement | null | false>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "lease_agreement" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAgreement(response.success ? response.normalizedMainModelResponse || false : false)
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(id)) datasource.get("v2", `lease-agreements/${id}`, {});
	}, [datasource, id]);

	// BEHIND THE `lease-agreements` FLAG (Sim, 22 Sep 2026). Hiding the masthead
	// entry is not access control — a typed URL or an old bookmark still lands
	// here, and without this the screen mounted and turned the API's 403 into
	// "This lease is not available", which reads like missing data rather than
	// "not yours". The API keeps its own gate; this is the UI half.
	//
	// BEFORE the record checks below, so a user without the flag is redirected
	// rather than shown the not-found state their own 403 produced.
	const allowed = useFeatureFlagGuard("lease-agreements");
	if (!allowed) return null;

	if (agreement === null) return <LoadingState />;

	if (agreement === false)
		return (
			<React.Fragment>
				<Breadcrumbs items={[{ title: "lease_agreements", link: "/lease-agreements" }]} />
				<Header title="lease_agreements" stamp="lease_agreements" />
				<Workspace id="lease-agreement">
					<View resource="lease_agreement">
						<div className="alert alert-light empty-state">{translate("lease_agreement_not_found")}</div>
					</View>
				</Workspace>
			</React.Fragment>
		);

	const title = agreement.tcode || (translate("lease_agreement") as string);

	return (
		<React.Fragment>
			<Breadcrumbs items={[{ title: "lease_agreements", link: "/lease-agreements" }, { title }]} />
			{/* PROPERTY AND TCODE ONLY (Sim, 24 Aug). Tenant, Current Amendment, Term
			    in Force and Units were header datapoints and came out — Header lays
			    them out inline with no separators, so they ran together
			    ("Al Beiruti CafeOriginal Lease"), and every one of them is already on
			    the Details tab. */}
			<Header title={title} subtitle={agreement.property_name || undefined} stamp="lease_agreements" />
			<Workspace
				id="lease-agreement"
				tabs={[
					// FIRST = DEFAULT. Do not reorder without meaning to change which tab
					// the page opens on.
					<AmendmentsTab name="amendments" key="amendments-tab" leaseAgreementId={agreement.id} />,
					<ContactsTab name="contacts" key="contacts-tab" leaseAgreementId={agreement.id} />,
					// NO ASSETS TAB (Sim, 26 Aug). An Asset belongs to an AMENDMENT now —
					// `Assets[]` arrives on a payload about one AmendmentID, so the set is
					// unique at that grain. Rolling the whole tree up here would list the
					// same asset once per term that restated it, so assets are shown where
					// they mean something: the project workspace's Assets tab, which reads
					// its own amendment's set. `?lease_agreement_id=` is gone from
					// V2::AssetsController with it.
					<TenantsTab name="tenants" key="tenants-tab" leaseAgreementId={agreement.id} />,
					// Beside Tenants: the units in force TODAY (the fold's answer) plus the
					// current-term pointer summary. Distinct from the Amendments tab, which
					// is the history that produced them.
					<TenanciesTab name="tenancies" key="tenancies-tab" agreement={agreement} />,
					// The same history, DRAWN: the amendment tree and where the current-term
					// pointer sits. Sim put it here, between Tenancies and Details.
					<TreeTab name="tree" key="tree-tab" agreement={agreement} />,
					<DetailsTab name="details" key="details-tab" agreement={agreement} />
				]}
			/>
		</React.Fragment>
	);
};

export default memo(Show);
